"use client";

import React, { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import telegram_image from "../../assets/telegram-analytics.png";
import instagram_image from "../../assets/instagram-analytics.png";
import whatsapp_image from "../../assets/whatsapp-analytics.png";
import facebook_image from "../../assets/facebook-analytics.png";
import mail_image from "../../assets/mail-analytics.png";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_URL = REST_API_URL;

export default function ClientsBySource() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState("Month");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const fetchClientsBySource = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(`${API_URL}/analytics/best-services/`, {
          headers: buildHeaders(),
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const clients = data?.clients || {};

        const formatted = [
          {
            name: t("home.sourceInstagram"),
            percent: clients.instagram ?? 0,
            image: instagram_image,
          },
          {
            name: t("home.sourceWhatsApp"),
            percent: clients.whatsapp ?? 0,
            image: whatsapp_image,
          },
          {
            name: t("home.sourceTelegram"),
            percent: clients.telegram ?? 0,
            image: telegram_image,
          },
          {
            name: t("home.sourceFacebook"),
            percent: clients.facebook ?? 0,
            image: facebook_image,
          },
          {
            name: t("home.sourceEmail"),
            percent: clients.email ?? 0,
            image: mail_image,
          },
        ];

        setData(formatted);
      } catch (err) {
        console.error("❌ Failed to fetch client sources:", err);
        setError(t("home.errorFetchFailedLoadClient"));
      } finally {
        setLoading(false);
      }
    };

    fetchClientsBySource();
  }, [token, navigate, t]);

  return (
    <div className="bg-white rounded-xl shadow p-6 w-full h-full flex flex-col justify-between">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[#302B2B] text-xl font-bold">{t("home.clientsBySourceTitle")}</h2>

        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 border border-[#9A99AB] text-[#9A99AB] text-sm rounded-xl px-4 py-2"
          >
            {t("home.byModeLabel", { mode: mode === "Day" ? t("home.optionDay") : t("home.optionMonth") })}
            <ChevronDownIcon
              className={`w-4 h-4 transform transition ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-32 rounded-lg bg-white shadow-md border border-neutral-200 overflow-hidden z-10">
              {["Day", "Month"].map((opt) => (
                <div
                  key={opt}
                  onClick={() => {
                    setMode(opt);
                    setOpen(false);
                  }}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#020637]/8 ${
                    mode === opt
                      ? "text-[#020637] font-medium"
                      : "text-neutral-700"
                  }`}
                >
                  {t("home.byModeLabel", { mode: opt === "Day" ? t("home.optionDay") : t("home.optionMonth") })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="text-red-500 text-sm mb-4 italic">{error}</p>
      )}

      {loading ? (
        <div className="flex justify-center items-center flex-1 min-h-[250px] text-neutral-400">
          {t("home.loadingSources")}
        </div>
      ) : (
        <div className="flex flex-col justify-between flex-1 gap-4">
          {data.map((src, i) => (
            <div
              key={i}
              className="flex items-center justify-between w-full h-[30px] gap-3"
            >
              <div className="flex-1 h-[30px] bg-[#F6F6F6] rounded-full relative overflow-hidden">
                <div
                  className="absolute left-0 top-0 h-full bg-[#020637] rounded-full transition-all duration-500"
                  style={{ width: `${src.percent}%` }}
                ></div>
              </div>

              <div className="flex items-center gap-2 min-w-[60px] justify-between w-[77px]">
                <div className="w-[30px] h-[30px] flex items-center justify-center">
                  <img
                    src={src.image}
                    alt={src.name}
                    className="w-[24px] h-[24px] object-contain"
                  />
                </div>
                <span className="text-[#302B2B] text-base font-normal">
                  {src.percent}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}